import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api, download } from '../api'
import { useMeta } from '../meta'
import Pager from '../components/Pager'
import Sparkline from '../components/Sparkline'
import {
  LineChart, PsychroChart, RampChart, RhythmChart, RuntimeChart,
} from '../components/Charts'
import type {
  ControlZone, DeviceDetail, InsightsPayload, Paged, ReadingRow, SetpointEvent,
} from '../types'
import { ACTOR_LABEL, ago, degC, fanOn, pct, staleness, when } from '../util'

/**
 * One thermostat, and everything recorded about it.
 *
 * History, insights and the control audit trail all live here rather than in
 * fleet-wide lists, because none of them mean anything detached from the zone
 * they describe: a decision is judged against that zone's band, and a runtime
 * total against that home's weather.
 */

const UNIT_KEY = 'therm.admin.unit'
type Tab = 'overview' | 'history' | 'insights' | 'control'
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'history', label: 'History' },
  { id: 'insights', label: 'Insights' },
  { id: 'control', label: 'Control' },
]

const STATUS_CHIP: Record<string, string> = {
  sent: 'on', shadow: '', pending: '', failed: 'danger',
}

export default function Thermostat() {
  const { homeId = '', deviceId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as Tab) || 'overview'
  const setTab = (t: Tab) => setParams(t === 'overview' ? {} : { tab: t },
                                       { replace: true })

  const [unit, setUnit] = useState(() => localStorage.getItem(UNIT_KEY) ?? 'f')
  const f = unit === 'f'
  const conv = (c: number) => (f ? (c * 9) / 5 + 32 : c)
  const deg = f ? '°F' : '°C'
  const t = (c: number | null | undefined, digits = 1) =>
    c == null ? '—' : `${conv(c).toFixed(digits)}${deg}`
  const pickUnit = (u: string) => { setUnit(u); localStorage.setItem(UNIT_KEY, u) }

  const { data: d, error, isLoading } = useQuery<DeviceDetail>({
    queryKey: ['device', deviceId],
    queryFn: () => api<DeviceDetail>(`/admin/devices/${deviceId}`),
    refetchInterval: 60_000,
  })

  if (isLoading) return <p className="muted">Loading…</p>
  if (error) return <div className="err">{(error as Error).message}</div>
  if (!d) return null

  return (
    <>
      <div className="crumbs">
        <Link to="/homes">Homes</Link><span>/</span>
        <Link to={`/homes/${homeId}`}>{d.home_name}</Link><span>/</span>
        <span>{d.display_name}</span>
      </div>

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ marginBottom: 0 }}>{d.display_name}</h1>
        <div className="seg">
          <button className={!f ? 'on' : ''} onClick={() => pickUnit('c')}>°C</button>
          <button className={f ? 'on' : ''} onClick={() => pickUnit('f')}>°F</button>
        </div>
      </div>
      <p className="sub">
        {d.room_name ? `${d.room_name} · ` : ''}{d.vendor} · {d.owner_email}
        {' · '}{d.city ?? 'no location'}
        {d.removed && ' · removed from the app, history retained'}
      </p>

      <div className="tabs">
        {TABS.map((x) => (
          <button key={x.id} className={tab === x.id ? 'on' : ''}
                  onClick={() => setTab(x.id)}>{x.label}</button>
        ))}
      </div>

      {tab === 'overview' && <Overview d={d} t={t} />}
      {tab === 'history' && <History deviceId={deviceId} t={t} unit={unit} />}
      {tab === 'insights' && <Insights deviceId={deviceId} conv={conv} deg={deg} />}
      {tab === 'control' && <Control deviceId={deviceId} t={t} />}
    </>
  )
}

// ---------------------------------------------------------------- overview

function Overview({ d, t }: { d: DeviceDetail; t: (c?: number | null, n?: number) => string }) {
  const qc = useQueryClient()
  const { poll_interval_s: poll } = useMeta()
  const s = staleness(d.reading_at, poll)
  const st = (d.control_state ?? {}) as Record<string, any>
  const cfg = (d.config ?? {}) as Record<string, any>

  const consent = useMutation({
    mutationFn: (allow: boolean) =>
      api(`/admin/devices/${d.id}/consent`, {
        method: 'PUT', body: JSON.stringify({ allow_control: allow }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device', d.id] }),
  })

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="lbl">Indoor</div>
          <div className="v">{t(d.temp)}</div>
          <div className="sub2">
            {d.humidity != null ? `${d.humidity.toFixed(0)}% RH` : '—'}
            {d.dew != null && ` · dew ${t(d.dew)}`}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Setpoint</div>
          <div className="v">{t(d.setpoint, 0)}</div>
          <div className="sub2">
            {d.mode ?? 'unknown'}{d.eco && d.eco !== 'OFF' ? ' · eco on' : ''}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Equipment</div>
          <div className="v" style={{ fontSize: 22 }}>
            {d.hvac && d.hvac !== 'OFF' ? d.hvac[0] + d.hvac.slice(1).toLowerCase() : 'Idle'}
          </div>
          <div className="sub2">
            fan {d.reading_at ? (fanOn(d.hvac, d.fan) ? 'on' : 'off') : 'not recorded'}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Outdoor</div>
          <div className="v">{t(d.out_temp)}</div>
          <div className="sub2">
            {d.out_dew != null ? `dew ${t(d.out_dew)}` : '—'} · {s.label}
          </div>
        </div>
      </div>

      {d.error && <div className="err">{d.error}</div>}

      <div className="panel">
        <header>Remote control</header>
        <div style={{ padding: '16px 18px', display: 'flex', gap: 16,
                      alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {d.allow_control
                ? 'The customer has allowed us to change this thermostat.'
                : 'Shadow only — the loop decides and sends nothing.'}
            </div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              This is the customer's switch. Changing it from here is an
              operator override and is written to the audit log.
              {d.consent_updated && ` Last changed ${ago(d.consent_updated)}.`}
            </div>
          </div>
          <button className={`btn ${d.allow_control ? 'ghost' : ''}`}
                  disabled={consent.isPending || d.removed}
                  onClick={() => consent.mutate(!d.allow_control)}>
            {d.allow_control ? 'Revoke control' : 'Allow control'}
          </button>
        </div>
      </div>

      <div className="panel">
        <header>Loop state</header>
        <div className="scroll">
          <table>
            <tbody>
              <Row k="Compressor" v={st.state === 'ON' ? 'Cooling'
                : st.state === 'OFF' ? 'Idle' : '—'} />
              <Row k="Comfort setpoint" v={t(st.nominal_c)}
                   sub={st.nominal_source === 'person' ? 'set by hand' : undefined} />
              <Row k="Dew band" v={st.dew_lo != null
                ? `${degC(st.dew_lo)} – ${degC(st.dew_hi)}` : '—'} />
              <Row k="Tick" v={cfg.tick_min ? `${cfg.tick_min} min` : '—'} />
              <Row k="Min cycle" v={cfg.min_on_min
                ? `${cfg.min_on_min} on / ${cfg.min_off_min} off` : '—'} />
              <Row k="State updated" v={ago(d.state_updated)} />
              <Row k="Model fitted" v={d.fitted_at ? when(d.fitted_at) : 'not yet'}
                   sub={d.rmse != null ? `RMSE ${d.rmse.toFixed(3)}` : undefined} />
              <Row k="Device id" v={d.vendor_device_id?.split('/').pop() ?? '—'} />
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

function Row({ k, v, sub }: { k: string; v: string; sub?: string }) {
  return (
    <tr>
      <th style={{ width: '14rem', position: 'static' }}>{k}</th>
      <td>{v}{sub && <div className="sublabel">{sub}</div>}</td>
    </tr>
  )
}

// ---------------------------------------------------------------- history

const HIST_LIMIT = 50

function History({ deviceId, t, unit }: {
  deviceId: string
  t: (c?: number | null, n?: number) => string
  unit: string
}) {
  const [view, setView] = useState<'samples' | 'changes'>('samples')
  const [hours, setHours] = useState(24)
  const [offset, setOffset] = useState(0)
  const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setOffset(0) }

  const samples = useQuery<Paged<ReadingRow> & { matched: number; total: number }>({
    queryKey: ['readings', deviceId, hours, offset],
    queryFn: () => api(`/admin/readings?device_id=${deviceId}&hours=${hours}`
      + `&limit=${HIST_LIMIT}&offset=${offset}`),
    enabled: view === 'samples',
  })
  const changes = useQuery<Paged<SetpointEvent>>({
    queryKey: ['setpoints', deviceId, hours, offset],
    queryFn: () => api(`/admin/history?device_id=${deviceId}&hours=${hours}`
      + `&limit=${HIST_LIMIT}&offset=${offset}`),
    enabled: view === 'changes',
  })

  const active = view === 'samples' ? samples : changes
  const rows = samples.data?.rows ?? []
  const events = changes.data?.rows ?? []
  const total = view === 'samples'
    ? samples.data?.matched ?? 0
    : changes.data?.total ?? 0

  const stamp = (iso: string, tz?: string) =>
    new Date(iso).toLocaleString(undefined, {
      timeZone: tz, month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <>
      <div className="row" style={{ marginBottom: '1rem' }}>
        <div className="seg">
          <button className={view === 'samples' ? 'on' : ''}
                  onClick={() => { setView('samples'); setOffset(0) }}>Samples</button>
          <button className={view === 'changes' ? 'on' : ''}
                  onClick={() => { setView('changes'); setOffset(0) }}>Changes</button>
        </div>
        <select value={hours} onChange={(e) => reset(setHours)(Number(e.target.value))}>
          <option value={24}>24 hours</option>
          <option value={168}>7 days</option>
          <option value={720}>30 days</option>
          <option value={8760}>1 year</option>
        </select>
        <button className="btn ghost"
                onClick={() => download(
                  `/admin/readings.csv?device_id=${deviceId}&hours=${hours}&unit=${unit}`)}>
          Download CSV
        </button>
        {samples.data && (
          <span className="muted">
            {samples.data.total.toLocaleString()} samples recorded in total
          </span>
        )}
      </div>

      {active.error && <div className="err">{(active.error as Error).message}</div>}

      <div className="panel">
        <header>{view === 'samples' ? 'Samples' : 'Setpoint changes'}</header>

        {active.isLoading ? <div className="empty">Loading…</div>
          : view === 'samples' ? (
            rows.length === 0 ? (
              <div className="empty">
                <h3>No samples in this window</h3>
                <p>The poller writes one row per thermostat per pass. Nothing
                   here means it has not run, or this zone is not connected.</p>
              </div>
            ) : (
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th rowSpan={2}>Time (local)</th>
                      <th colSpan={3} className="grp sep">Indoor</th>
                      <th colSpan={3} className="grp sep">Outdoor</th>
                      <th rowSpan={2} className="sep">Mode</th>
                      <th rowSpan={2}>HVAC</th>
                      <th rowSpan={2}>Fan</th>
                      <th rowSpan={2} className="num">Setpoint</th>
                    </tr>
                    <tr>
                      <th className="num sep">Temp</th><th className="num">Hum %</th>
                      <th className="num">Dew</th>
                      <th className="num sep">Temp</th><th className="num">Hum %</th>
                      <th className="num">Dew</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="muted">{stamp(r.ts, r.timezone)}</td>
                        {r.error ? (
                          <td colSpan={9} className="wrap muted">
                            <span className="chip danger">no reading</span> {r.error}
                          </td>
                        ) : (
                          <>
                            <td className="num sep">{t(r.temp)}</td>
                            <td className="num">{r.humidity?.toFixed(0) ?? '—'}</td>
                            <td className="num">{t(r.dew)}</td>
                            <td className="num sep">{t(r.out_temp)}</td>
                            <td className="num">{r.out_humidity?.toFixed(0) ?? '—'}</td>
                            <td className="num">{t(r.out_dew)}</td>
                            <td className="sep">
                              {r.mode ?? '—'}
                              {r.eco && r.eco !== 'OFF' && <div className="sublabel">eco</div>}
                            </td>
                            <td>
                              {r.hvac && r.hvac !== 'OFF'
                                ? <span className="chip on">{r.hvac.toLowerCase()}</span>
                                : <span className="muted">idle</span>}
                            </td>
                            <td>
                              {fanOn(r.hvac, r.fan)
                                ? <span className="chip on">on</span>
                                : <span className="muted">off</span>}
                            </td>
                            <td className="num">{t(r.setpoint, 0)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : events.length === 0 ? (
            <div className="empty">
              <h3>No setpoint changes in this window</h3>
              <p>Nobody moved this thermostat and the loop sent nothing. The
                 samples view shows what it was holding.</p>
            </div>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr><th>When (local)</th><th>Who changed it</th>
                      <th className="num">From → To</th><th>Why</th></tr>
                </thead>
                <tbody>
                  {events.map((e, i) => (
                    <tr key={i}>
                      <td className="muted">{when(e.ts, e.timezone)}</td>
                      <td>
                        <span className={`chip bare ${e.actor === 'loop' ? 'on' : ''}`}>
                          {ACTOR_LABEL[e.actor] ?? e.actor}
                        </span>
                        {e.actor_email && e.actor_email !== e.owner_email && (
                          <div className="sublabel">{e.actor_email}</div>
                        )}
                      </td>
                      <td className="num">
                        {e.from_c != null ? `${t(e.from_c, 0)} → ` : ''}{t(e.to_c, 0)}
                      </td>
                      <td className="wrap muted">{e.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <Pager total={total} limit={HIST_LIMIT} offset={offset}
               onOffset={setOffset}
               noun={view === 'samples' ? 'sample' : 'change'} />
      </div>
    </>
  )
}

// ---------------------------------------------------------------- insights

function Insights({ deviceId, conv, deg }: {
  deviceId: string; conv: (c: number) => number; deg: string
}) {
  const [hours, setHours] = useState(168)
  const { data, error, isLoading } = useQuery<InsightsPayload>({
    queryKey: ['insights', deviceId, hours],
    queryFn: () => api<InsightsPayload>(
      `/admin/insights?device_id=${deviceId}&hours=${hours}`),
  })

  const hhmm = (m: number | null | undefined) =>
    m == null ? '—'
      : m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`
        : `${Math.round(m)}m`

  const s = data?.stats
  const series = data?.series ?? []

  return (
    <>
      <div className="row" style={{ marginBottom: '1rem' }}>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          <option value={24}>Last 24 hours</option>
          <option value={168}>Last 7 days</option>
          <option value={720}>Last 30 days</option>
          <option value={8760}>Last year</option>
        </select>
        {data && (
          <span className="muted">
            {s?.samples?.toLocaleString() ?? 0} samples
            {data.bucket_s > 0 && ` · averaged into ${data.bucket_s / 60}-min points`}
          </span>
        )}
      </div>

      {error && <div className="err">{(error as Error).message}</div>}
      {isLoading && <p className="muted">Loading…</p>}

      {s && s.samples === 0 && (
        <div className="panel"><div className="empty">
          <h3>Nothing logged in this range</h3>
          <p>Widen the window, or check that the poller is running.</p>
        </div></div>
      )}

      {s && s.samples > 0 && data && (
        <>
          <div className="stats">
            <div className="stat">
              <div className="lbl">Cooling runtime</div>
              <div className="v">{hhmm(s.cool_min)}</div>
              <div className="sub2">
                {(s.heat_min ?? 0) > 0 && `${hhmm(s.heat_min)} heating · `}
                {s.duty}% duty cycle
                {(s.fan_only_min ?? 0) > 0 && ` · ${hhmm(s.fan_only_min)} blower alone`}
              </div>
            </div>
            <div className="stat">
              <div className="lbl">Avg indoor</div>
              <div className="v">{conv(s.in_t ?? 0).toFixed(1)}{deg}</div>
              <div className="sub2">dew point {conv(s.in_d ?? 0).toFixed(1)}{deg}</div>
            </div>
            <div className="stat">
              <div className="lbl">Avg outdoor</div>
              <div className="v">{conv(s.out_t ?? 0).toFixed(1)}{deg}</div>
              <div className="sub2">dew point {conv(s.out_d ?? 0).toFixed(1)}{deg}</div>
            </div>
            <div className="stat">
              <div className="lbl">Covered</div>
              <div className="v">{hhmm(s.covered_min)}</div>
              <div className="sub2">of the window requested</div>
            </div>
          </div>

          {/* The moisture story first. Runtime and temperature are what a
              thermostat already reports; this is the part that explains why a
              satisfied thermostat can still be leaving a house uncomfortable. */}
          <Moisture m={data.moisture} deg={deg} />
          {data.psychro && (
            <PsychroChart rows={series} psy={data.psychro} conv={conv} deg={deg}
                          bucketS={data.bucket_s} />
          )}
          <LineChart title="Indoor vs outdoor temperature"
            caption="Setpoint included, so a call for cooling lines up with the
                     gap between the two. All three share one axis. The strip
                     underneath shows when the compressor was running."
            rows={series} unit={deg} conv={conv} bucketS={data.bucket_s} activity
            series={[{ key: 'in_t', label: 'Indoor', slot: 1 },
                     { key: 'out_t', label: 'Outdoor', slot: 2 },
                     { key: 'sp', label: 'Setpoint', slot: 3, step: true }]} />
          <LineChart title="Dew point"
            caption="Indoor dew point above outdoor means the house is making
                     humidity faster than the system removes it."
            rows={series} unit={deg} conv={conv} bucketS={data.bucket_s} activity
            series={[{ key: 'in_d', label: 'Indoor', slot: 1 },
                     { key: 'out_d', label: 'Outdoor', slot: 2 }]} />
          <LineChart title="Relative humidity"
            caption="Its own chart, not a second axis on the one above: percent
                     and degrees do not share a scale."
            rows={series} unit="%" digits={0} bucketS={data.bucket_s}
            series={[{ key: 'in_h', label: 'Indoor', slot: 1 },
                     { key: 'out_h', label: 'Outdoor', slot: 2 }]} />

          <Cycles c={data.cycles} deg={deg} />
          <RampChart rows={data.cycles.recent} deg={deg} />
          <RuntimeChart rows={data.daily} />
          <RhythmChart rows={data.hourly} conv={conv} deg={deg} />
          <DecisionMix rows={data.decisions} />
        </>
      )}
    </>
  )
}

/**
 * Whether the house is making moisture faster than the equipment removes it.
 *
 * `pct_above_ceiling_idle` is the one to read first and the only number here
 * that indicts anything: it is the share of the window spent over the dew-point
 * ceiling *with the compressor off*. Those are the hours a temperature-only
 * thermostat had nothing to say, because by temperature the house was finished.
 */
function Moisture({ m, deg }: {
  m: InsightsPayload['moisture']; deg: string
}) {
  // A dew-point *difference* is a delta, so it converts by ratio, not by the
  // F = C*9/5 + 32 offset. Running it through `conv` would add 32 to a gap.
  const d = (v: number | null | undefined) =>
    v == null ? '—' : `${(deg === '°F' ? v * 1.8 : v).toFixed(2)}${deg}`
  const pct = (v: number | null | undefined) => v == null ? '—' : `${v}%`
  return (
    <div className="panel">
      <header>Moisture balance</header>
      <div className="stats">
        <div className="stat">
          <div className="lbl">Wetter indoors than out</div>
          <div className="v">{pct(m.pct_wetter_inside)}</div>
          <div className="sub2">of samples · mean excess {d(m.mean_excess)}</div>
        </div>
        <div className="stat">
          <div className="lbl">Above the dew ceiling</div>
          <div className="v">{pct(m.pct_above_ceiling)}</div>
          <div className="sub2">peak excess over outdoor {d(m.peak_excess)}</div>
        </div>
        <div className="stat">
          <div className="lbl">Above it, nothing running</div>
          <div className="v" style={{ color: (m.pct_above_ceiling_idle ?? 0) > 20
                                        ? 'var(--danger)' : undefined }}>
            {pct(m.pct_above_ceiling_idle)}
          </div>
          <div className="sub2">the hours a thermostat cannot see</div>
        </div>
      </div>
    </div>
  )
}

/**
 * The compressor as discrete runs rather than a duty percentage.
 *
 * Six short starts and one long run reach the same duty and do opposite things
 * to a house: the coil only condenses once it is cold and wet, so a run that
 * ends before that point has done sensible cooling and thrown the latent work
 * away — and then re-evaporated its own condensate back into the house when the
 * fan stopped.
 */
function Cycles({ c, deg }: {
  c: InsightsPayload['cycles']; deg: string
}) {
  const d = (v: number | null | undefined) =>
    v == null ? '—' : `${(deg === '°F' ? v * 1.8 : v).toFixed(2)}${deg}`
  if (!c.n) {
    return (
      <div className="panel">
        <header>Compressor cycles</header>
        <div className="empty">
          <h3>No cooling in this window</h3>
          <p>Nothing to reconstruct. The actuator curves and the latent ramp both
             need complete runs, which is why a mild fortnight leaves a zone
             unidentified however many samples it has.</p>
        </div>
      </div>
    )
  }
  return (
    <div className="panel">
      <header>Compressor cycles</header>
      <div className="stats">
        <div className="stat">
          <div className="lbl">Runs</div>
          <div className="v">{c.n}</div>
          <div className="sub2">
            busiest hour {c.busiest ?? '—'} start{c.busiest === 1 ? '' : 's'}
            {(c.busiest ?? 0) > 6 ? ' · over the safety limit' : ''}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Typical length</div>
          <div className="v">{c.median_min ?? '—'}<span style={{fontSize:15}}>m</span></div>
          <div className="sub2">mean {c.mean_min ?? '—'}m · longest {c.longest_min ?? '—'}m</div>
        </div>
        <div className="stat">
          <div className="lbl">Too short to dry</div>
          <div className="v" style={{ color: c.short_cycles > 0 ? 'var(--danger)' : undefined }}>
            {c.short_cycles}
          </div>
          <div className="sub2">runs under 10 min</div>
        </div>
        <div className="stat">
          <div className="lbl">Dew removed per run</div>
          <div className="v" style={{ fontSize: 22 }}>{d(c.mean_dew_drop)}</div>
          <div className="sub2">temperature {d(c.mean_temp_drop)}</div>
        </div>
      </div>
    </div>
  )
}

/** What the loop actually decided, by its stable code. */
function DecisionMix({ rows }: { rows: InsightsPayload['decisions'] }) {
  if (!rows.length) return null
  const total = rows.reduce((a, r) => a + r.n, 0)
  return (
    <div className="panel">
      <header>Decisions in this window</header>
      <div className="scroll">
        <table>
          <thead>
            <tr><th>Code</th><th>Action</th><th className="num">Count</th>
                <th className="num">Share</th><th className="num">Sent</th>
                <th>Last</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <span className="chip bare">{r.reason_code ?? 'unspecified'}</span>
                </td>
                <td>{r.action}</td>
                <td className="num">{r.n}</td>
                <td className="num">{(100 * r.n / total).toFixed(0)}%</td>
                {/* Zero here is the expected reading, not a gap: until a zone is
                    armed every decision is shadow by design. */}
                <td className="num">{r.sent}</td>
                <td className="muted">{when(r.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- control

const CTRL_LIMIT = 25

function Control({ deviceId, t }: {
  deviceId: string; t: (c?: number | null, n?: number) => string
}) {
  const [hours, setHours] = useState(24)
  const [offset, setOffset] = useState(0)

  const { data: z, error, isLoading } = useQuery<ControlZone>({
    queryKey: ['control', deviceId, hours, offset],
    queryFn: () => api<ControlZone>(`/admin/control/${deviceId}?hours=${hours}`
      + `&limit=${CTRL_LIMIT}&offset=${offset}`),
  })

  if (isLoading) return <p className="muted">Loading…</p>
  if (error) return <div className="err">{(error as Error).message}</div>
  if (!z) return null

  const st = (z.control_state ?? {}) as Record<string, any>
  // All three gates, named. "Shadow" with no explanation sends an operator
  // hunting through config for a switch that is somewhere else entirely.
  const blocked = [
    !z.arm && 'the global kill switch is off',
    !z.allow_control && 'the customer has not allowed control',
  ].filter(Boolean) as string[]

  return (
    <>
      <div className="row" style={{ marginBottom: '1rem' }}>
        <select value={hours}
                onChange={(e) => { setHours(Number(e.target.value)); setOffset(0) }}>
          <option value={6}>6 hours</option>
          <option value={24}>24 hours</option>
          <option value={72}>3 days</option>
          <option value={168}>7 days</option>
        </select>
        <span className={`chip ${blocked.length === 0 ? 'on' : ''}`}>
          {blocked.length === 0 ? 'Armed' : 'Shadow'}
        </span>
        <span className="muted">
          {blocked.length === 0
            ? 'Decisions reach the thermostat.'
            : `Deciding and sending nothing — ${blocked.join(' and ')}.`}
        </span>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="lbl">Compressor</div>
          <div className="v" style={{ fontSize: 22 }}>
            {st.state === 'ON' ? 'Cooling' : st.state === 'OFF' ? 'Idle' : '—'}
          </div>
          <div className="sub2">state {ago(z.state_updated)}</div>
        </div>
        <div className="stat">
          <div className="lbl">Duty</div>
          <div className="v">{pct(z.duty)}</div>
          <div className="sub2">last {z.hours} h, time-integrated</div>
        </div>
        <div className="stat">
          <div className="lbl">Setpoint writes</div>
          <div className="v">{z.writes}</div>
          <div className="sub2">
            {z.errors > 0 ? `${z.errors} failed` : 'no send errors'}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Dew point</div>
          <div className="v" style={{ fontSize: 22 }}>{degC(z.dew_mean)}</div>
          <div className="sub2">
            <Sparkline points={z.series ?? []}
              lo={typeof st.dew_lo === 'number' ? st.dew_lo : undefined}
              hi={typeof st.dew_hi === 'number' ? st.dew_hi : undefined} />
          </div>
        </div>
      </div>

      <Learning z={z} />

      <div className="panel">
        <header>Decisions</header>
        {z.recent.length === 0 ? (
          <div className="empty">
            <h3>No decisions in this window</h3>
            <p>The loop runs on the quarter hour. A zone with no identified
               model yet produces nothing until it has enough history.</p>
          </div>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr><th>When</th><th>Status</th><th>Action</th>
                    <th className="num">From → To</th><th className="num">Dew</th>
                    <th>Code</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {z.recent.map((c, i) => (
                  <tr key={i}>
                    <td className="muted">{when(c.ts)}</td>
                    <td><span className={`chip bare ${STATUS_CHIP[c.status] ?? ''}`}>
                      {c.status}</span></td>
                    <td>{c.action}</td>
                    <td className="num">
                      {c.to_setpoint_c != null
                        ? `${t(c.from_setpoint_c, 0)} → ${t(c.to_setpoint_c, 0)}`
                        : '—'}
                    </td>
                    <td className="num">{degC(c.indoor_dew_c)}</td>
                    {/* The code and the prose are not redundant. The code is
                        what makes a fortnight of decisions countable; the prose
                        is what makes one of them reviewable. A zone whose codes
                        are all `unspecified` is running old worker code. */}
                    <td>
                      {c.reason_code
                        ? <span className="chip bare">{c.reason_code}</span>
                        : <span className="muted">—</span>}
                      {c.supervised && <div className="err"
                        style={{ margin: '.3rem 0 0' }}>supervisor</div>}
                    </td>
                    <td className="wrap">
                      {c.reason}
                      {c.error && <div className="err"
                        style={{ margin: '.3rem 0 0' }}>{c.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={z.total} limit={CTRL_LIMIT} offset={offset}
               onOffset={setOffset} noun="decision" />
      </div>
    </>
  )
}


/**
 * What the learner has worked out about this zone — and what it refuses to.
 *
 * The refusals are the half an operator needs. A zone sitting in monitor for
 * three weeks is sitting there for a stated reason ("never observed cooling, so
 * the cooling authority is not identified at all"), and that sentence is the
 * work item. Without it, monitor looks like the loop being broken.
 *
 * Empty is a legitimate state and is rendered as one. Every zone spends its
 * first fortnight here, and greying it out would make the normal case look like
 * a fault.
 */
function Learning({ z }: { z: ControlZone }) {
  const l = z.learned
  const rc2 = (l?.rc2 ?? {}) as Record<string, unknown>
  const act = (l?.actuator ?? {}) as Record<string, Record<string, number | null>>
  const ramp = act.latent_ramp ?? {}
  const sens = act.sensible ?? {}
  const n = (v: unknown, d = 2) =>
    typeof v === 'number' && Number.isFinite(v) ? v.toFixed(d) : '—'

  return (
    <div className="panel">
      <header>What it has learned</header>

      {z.mix.length > 0 && (
        <div style={{ padding: '.9rem 1rem', display: 'flex', gap: '.4rem',
                      flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          {z.mix.map((m) => (
            <span key={m.reason_code} className="chip bare">
              {m.reason_code} · {m.n}
            </span>
          ))}
        </div>
      )}

      {l == null || (l.fit_quality == null && l.n_labels == null
                     && l.t_air == null) ? (
        <div className="empty">
          <h3>Nothing identified yet</h3>
          <p>The learner needs cycles it did not cause — the building fit wants
             both a rising and a falling temperature, and the actuator curves
             want complete compressor cycles. Every zone starts here.</p>
        </div>
      ) : (
        <>
          <div className="stats">
            <div className="stat">
              <div className="lbl">Building fit</div>
              <div className="v">{n(l.fit_quality)}</div>
              <div className="sub2">
                {l.rmse != null ? `RMSE ${n(l.rmse)} °C` : 'not scored'}
                {l.fitted_at ? ` · ${ago(l.fitted_at)}` : ''}
              </div>
            </div>
            <div className="stat">
              <div className="lbl">Time constants</div>
              <div className="v" style={{ fontSize: 20 }}>
                {n(rc2.tau_fast_h, 1)} / {n(rc2.tau_slow_h, 1)} h
              </div>
              <div className="sub2">
                air / mass{rc2.fast_resolved === false
                  ? ' · fast pole unresolved' : ''}
                {typeof sens.cool_c_h === 'number'
                  ? ` · cools ${n(sens.cool_c_h, 1)} °C/h` : ''}
              </div>
            </div>
            <div className="stat">
              <div className="lbl">Latent ramp</div>
              <div className="v" style={{ fontSize: 20 }}>
                {n(ramp.tau_min, 0)} min
              </div>
              {/* The single most consequential learned parameter: below this,
                  running the compressor cools without drying. */}
              <div className="sub2">
                before the coil dries · R² {n(ramp.r2)}
              </div>
            </div>
            <div className="stat">
              <div className="lbl">Comfort labels</div>
              <div className="v">{l.n_labels ?? 0}</div>
              <div className="sub2">
                {(l.n_labels ?? 0) > 0
                  ? `weight ${n(l.weight_seen, 1)} · ${ago(l.comfort_at)}`
                  : 'population prior only'}
              </div>
            </div>
          </div>

          {l.t_air != null && (
            <div style={{ padding: '.9rem 1rem',
                          borderTop: '1px solid var(--border)' }}>
              <div className="lbl">Estimator</div>
              <div className="sub2" style={{ marginTop: '.3rem' }}>
                air {n(l.t_air)} °C · mass {n(l.t_mass)} °C · unmodelled gain
                {' '}{n(l.q_internal)} °C/h · ±{n(l.sd_t_air)} °C
                {l.estimated_at ? ` · ${ago(l.estimated_at)}` : ''}
              </div>
            </div>
          )}

          {(l.fit_reasons ?? []).length > 0 && (
            <div style={{ padding: '.9rem 1rem',
                          borderTop: '1px solid var(--border)' }}>
              <div className="lbl">Why it is not acting on this yet</div>
              <ul style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem' }}>
                {(l.fit_reasons ?? []).map((r, i) => (
                  <li key={i} className="muted" style={{ marginTop: '.2rem' }}>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {z.vetoes.length > 0 && (
        <div style={{ padding: '.9rem 1rem',
                      borderTop: '1px solid var(--border)' }}>
          <div className="lbl">Supervisor interventions</div>
          <div className="sub2" style={{ marginTop: '.3rem' }}>
            {z.vetoes.map((v) => `${v.n}× ${v.rule} (${v.action})`).join(' · ')}
          </div>
        </div>
      )}
    </div>
  )
}
